import { useState, useEffect, useMemo } from 'react';
import { showAlert } from '@/components/AppDialog';
import { doc, getDoc } from 'firebase/firestore';
import { saveAdminConfig } from '@/utils/adminConfig';
import { db } from '../firebase';
import AdminLayout from '../components/AdminLayout';
import Dh from '../components/Dh';
import { logAdminAction } from '@/utils/audit';
import AccountingPackagesEditor, { DEFAULT_PACKAGES, type Pkg } from '../components/AccountingPackagesEditor';
import { Save, CheckCircle2, AlertTriangle, Info, Tag } from 'lucide-react';

// ── Types ──────────────────────────────────────────────────────────────────────
type Fees  = { vatReg: number; vatFiling: number; ctReg: number; ctFiling: number };
type Bands = Record<string, number>;
type Flags = [number, number, number, number, number]; // [books, vatReg, vatFiling, ctReg, ctFiling]
// Subscription plans (Pulse / Total Compliance / Business-in-a-Box) live in
// components/AccountingPackagesEditor — the same editor is mirrored on the Package
// Pricing page. Both persist to pricing/current.packages.

// ── Defaults ───────────────────────────────────────────────────────────────────
const DEFAULT_FEES: Fees  = { vatReg: 500, vatFiling: 1000, ctReg: 500, ctFiling: 2000 };
const DEFAULT_BANDS: Bands = { 'Up to 100': 1500, '100 – 200': 2000, '200 – 500': 3000, '500+': 5000 };
const BAND_KEYS    = ['Up to 100', '100 – 200', '200 – 500', '500+'];

// ── Package combo generation (mirrors Services.tsx exactly) ────────────────────
const CODES = ['VR','VF','CR','CF','B'];
const RANK: Record<string,number> = { VR:0, VF:1, CR:2, CF:3, B:4 };

function toFlags(set: string[]): Flags {
  return [set.includes('B')?1:0, set.includes('VR')?1:0, set.includes('VF')?1:0, set.includes('CR')?1:0, set.includes('CF')?1:0] as Flags;
}
function sameSet(a:string[],b:string[]){ return a.length===b.length&&a.every(x=>b.includes(x)); }
function combTier(set:string[]){
  if(set.length===1) return 1;
  if(set.length===2){ if(sameSet(set,['VR','VF'])) return 2; if(sameSet(set,['CR','CF'])) return 2.1; return 3; }
  return set.length+1;
}
function rankKey(set:string[]){ return set.map(c=>RANK[c]).sort((x,y)=>(x??0)-(y??0)).join(''); }
function pkgName(f:Flags): string {
  const [b,vr,vf,cr,cf] = f;
  if(b&&vr&&vf&&cr&&cf) return 'Full – Books + VAT + CT';
  const p:string[]=[];
  if(vr&&vf) p.push('VAT (Reg + Filing)'); else if(vr) p.push('VAT Registration'); else if(vf) p.push('VAT Filing');
  if(cr&&cf) p.push('Corp. Tax (Reg + Filing)'); else if(cr) p.push('Corp. Tax Registration'); else if(cf) p.push('Corp. Tax Filing');
  if(b) p.push('Bookkeeping');
  return p.join(' + ');
}
const COMBOS: { flags: Flags; name: string }[] = (() => {
  const sets: string[][] = [];
  for(let m=1;m<32;m++){ const s:string[]=[]; CODES.forEach((c,i)=>{ if(m&(1<<i)) s.push(c); }); sets.push(s); }
  sets.sort((a,b)=>combTier(a)-combTier(b)||rankKey(a).localeCompare(rankKey(b)));
  return sets.map(s=>{ const f=toFlags(s); return { flags:f, name:pkgName(f) }; });
})();

// ── Price calculation ──────────────────────────────────────────────────────────
function calcPrice(flags: Flags, fees: Fees, bands: Bands, band: string) {
  let once=0, month=0, qtr=0, year=0;
  if(flags[0]) month += bands[band]??0;
  if(flags[1]) once  += fees.vatReg;
  if(flags[2]) qtr   += fees.vatFiling;
  if(flags[3]) once  += fees.ctReg;
  if(flags[4]) year  += fees.ctFiling;
  let hv=0, hu='', k='';
  if(month>0)     { hv=month; hu='/ month';        k='m'; }
  else if(once>0) { hv=once;  hu='one-time setup'; k='o'; }
  else if(qtr>0)  { hv=qtr;   hu='/ quarter';      k='q'; }
  else if(year>0) { hv=year;  hu='/ year';         k='y'; }
  return { once, month, qtr, year, hv, hu, k };
}

// ── Money display ──────────────────────────────────────────────────────────────
function Money({ aed }: { aed: number }) {
  return <><Dh em="0.8"/>{aed.toLocaleString('en-US')}</>;
}

// ── Component ──────────────────────────────────────────────────────────────────
export default function AdminPricingControl() {
  const [fees,    setFees]    = useState<Fees>(DEFAULT_FEES);
  const [bands,   setBands]   = useState<Bands>(DEFAULT_BANDS);
  const [packages, setPackages] = useState<Pkg[]>(DEFAULT_PACKAGES);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [savedAt, setSavedAt] = useState<string|null>(null);
  const [loadError, setLoadError] = useState<string|null>(null);

  // Preview state
  const [pkgIdx, setPkgIdx]   = useState(() => COMBOS.findIndex(c => c.flags.join('')==='11111'));
  const [pvBand, setPvBand]   = useState('100 – 200');

  // Load from Firestore
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'pricing', 'current'));
        if (snap.exists()) {
          const d = snap.data();
          if (d.vatReg) setFees({ vatReg: d.vatReg, vatFiling: d.vatFiling, ctReg: d.ctReg, ctFiling: d.ctFiling });
          if (d.bands)  setBands(d.bands);
          if (Array.isArray(d.packages) && d.packages.length) setPackages(d.packages);
        }
      } catch(e: any) {
        console.error('Failed to load pricing/current:', e);
        if (e?.code === 'permission-denied') {
          setLoadError('Firestore permission denied. Saved pricing could not be loaded. Showing defaults.');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Save
  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAdminConfig('pricing/current', {
        vatReg: fees.vatReg, vatFiling: fees.vatFiling,
        ctReg: fees.ctReg, ctFiling: fees.ctFiling,
        bands, packages,
      });
      setSavedAt(new Date().toLocaleTimeString());
      logAdminAction('config.update', 'pricing/current', { keys: ['fees', 'bands', 'packages'] });
    } catch(e) {
      console.error('Save failed:', e);
      showAlert('Save failed. Check console.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => { setFees(DEFAULT_FEES); setBands(DEFAULT_BANDS); setPackages(DEFAULT_PACKAGES); setSavedAt(null); };
  const updateFee   = (k: keyof Fees, v: number) => { setFees(prev => ({ ...prev, [k]: v })); setSavedAt(null); };
  const updateBand  = (k: string, v: number)     => { setBands(prev => ({ ...prev, [k]: v })); setSavedAt(null); };
  const updatePackages = (next: Pkg[]) => { setPackages(next); setSavedAt(null); };

  // Live preview
  const pvFlags = COMBOS[pkgIdx]?.flags ?? ([1,1,1,1,1] as Flags);
  const pvPrice = useMemo(() => calcPrice(pvFlags, fees, bands, pvBand), [pvFlags, fees, bands, pvBand]);
  const pvBooksOn = pvFlags[0] === 1;

  const FEE_ROWS: { key: keyof Fees; label: string; freq: string }[] = [
    { key: 'vatReg',    label: 'VAT Registration',          freq: 'one-time'  },
    { key: 'vatFiling', label: 'VAT Filing',                freq: 'quarterly' },
    { key: 'ctReg',     label: 'Corporate Tax Registration', freq: 'one-time'  },
    { key: 'ctFiling',  label: 'Corporate Tax Filing',       freq: 'annual'    },
  ];

  const LAYOUT = {
    section: 'Pricing',
    title: 'Accounting Pricing',
    icon: Tag,
    subtitle: "Edit fees once: every client's Services page and the public pricing page update instantly.",
  };

  if (loading) return <AdminLayout {...LAYOUT} loading loadingLabel="Loading pricing…" />;

  return (
    <AdminLayout {...LAYOUT}>
      {loadError && (
        <div className="flex gap-2 items-start bg-red-50 border border-red-300 rounded-xl px-4 py-3 mb-4 text-xs font-bold text-red-800">
          <AlertTriangle className="w-4 h-4 flex-none stroke-red-500 mt-0.5" />
          <span>{loadError}</span>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-5 items-start">

        {/* ── LEFT: fee editor ─────────────────────────────────────────── */}
        <div className="space-y-4">

          {/* Tax services */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-[var(--brand)] to-[var(--brand-d)]" />
            <div className="p-5">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Tax services</div>
              <div className="grid grid-cols-[1fr_160px_100px] gap-x-4 gap-y-3 items-center">
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Service</div>
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Fee</div>
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Frequency</div>
                {FEE_ROWS.map(({ key, label, freq }) => (
                  <>
                    <div key={key+'-l'} className="font-semibold text-sm text-gray-900">{label}</div>
                    <div key={key+'-i'} className="relative flex items-center">
                      <span className="absolute left-3 pointer-events-none text-gray-400">
                        <Dh em="0.75" style={{ marginRight: 0 }} />
                      </span>
                      <input
                        type="number" min={0} value={fees[key]}
                        onChange={e => updateFee(key, Math.max(0, +e.target.value || 0))}
                        className="w-full border-2 border-gray-200 rounded-xl pl-7 pr-3 py-2 text-sm font-bold bg-[var(--brand-soft)] text-gray-900 focus:outline-none focus:border-[var(--brand)]"
                      />
                    </div>
                    <div key={key+'-f'} className="text-xs text-gray-400 font-semibold">{freq}</div>
                  </>
                ))}
              </div>
            </div>
          </div>

          {/* Bookkeeping bands */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-[var(--brand)] to-[var(--brand-d)]" />
            <div className="p-5">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Bookkeeping bands (per month)</div>
              <div className="grid grid-cols-[1fr_160px_100px] gap-x-4 gap-y-3 items-center">
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Transactions / month</div>
                <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Fee</div>
                <div />
                {BAND_KEYS.map(k => (
                  <>
                    <div key={k+'-l'} className="font-semibold text-sm text-gray-900">{k}</div>
                    <div key={k+'-i'} className="relative flex items-center">
                      <span className="absolute left-3 pointer-events-none text-gray-400">
                        <Dh em="0.75" style={{ marginRight: 0 }} />
                      </span>
                      <input
                        type="number" min={0} value={bands[k] ?? 0}
                        onChange={e => updateBand(k, Math.max(0, +e.target.value || 0))}
                        className="w-full border-2 border-gray-200 rounded-xl pl-7 pr-3 py-2 text-sm font-bold bg-[var(--brand-soft)] text-gray-900 focus:outline-none focus:border-[var(--brand)]"
                      />
                    </div>
                    <div key={k+'-f'} className="text-xs text-gray-400 font-semibold">/ month</div>
                  </>
                ))}
              </div>
            </div>
          </div>

          {/* Subscription plans — shared editor, mirrored on the Package Pricing page */}
          <AccountingPackagesEditor packages={packages} onChange={updatePackages} />

          {/* Save / Reset */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 bg-[var(--brand)] hover:bg-[var(--brand-h)] disabled:opacity-60 text-[var(--brand-on)] font-extrabold text-sm px-5 py-2.5 rounded-xl shadow-md transition-all hover:-translate-y-0.5 active:translate-y-0"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save pricing'}
            </button>
            <button
              onClick={handleReset}
              className="border border-gray-200 bg-white text-gray-600 font-bold text-sm px-4 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Reset to defaults
            </button>
            {savedAt && (
              <span className="flex items-center gap-1.5 text-green-600 font-bold text-xs">
                <CheckCircle2 className="w-4 h-4" />
                Saved · clients see new prices as of {savedAt}
              </span>
            )}
          </div>
        </div>

        {/* ── RIGHT: client preview ─────────────────────────────────────── */}
        <div className="xl:sticky xl:top-[88px]">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-[var(--brand)] to-[var(--brand-d)]" />
            <div className="p-5">

              <div className="flex gap-2 items-start bg-[var(--brand-soft)] border border-[var(--brand-line)] rounded-xl px-4 py-3 mb-4 text-xs font-bold text-[var(--brand-d)]">
                <Info className="w-4 h-4 flex-none stroke-[var(--brand)] mt-0.5" />
                <span>Client preview: what clients see on their Services page with the current fees.</span>
              </div>

              {/* Package selector */}
              <div className="mb-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider block mb-1">Package</label>
                <select
                  value={pkgIdx}
                  onChange={e => setPkgIdx(+e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm font-bold text-gray-900 bg-white focus:outline-none focus:border-[var(--brand)]"
                >
                  {COMBOS.map((c, i) => (
                    <option key={i} value={i}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Band selector */}
              <div className="mb-3">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider block mb-1">Bookkeeping volume</label>
                <select
                  value={pvBand}
                  onChange={e => setPvBand(e.target.value)}
                  disabled={!pvBooksOn}
                  className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm font-bold text-gray-900 bg-white focus:outline-none focus:border-[var(--brand)] disabled:bg-gray-50 disabled:text-gray-400"
                >
                  {BAND_KEYS.map(k => <option key={k} value={k}>{k} / mo</option>)}
                </select>
              </div>

              {/* Hero price */}
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-baseline gap-2 mb-0.5">
                  <span className="text-3xl font-black text-gray-900 tracking-tight">
                    {pvPrice.hv ? <Money aed={pvPrice.hv}/> : '-'}
                  </span>
                  <span className="text-xs text-gray-400 font-bold">{pvPrice.hu}</span>
                </div>

                {/* Breakdown */}
                {pvPrice.hv > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {pvPrice.once  > 0 && <div className="flex justify-between text-xs font-semibold text-gray-600"><span>One-time setup</span><b className="text-gray-900 font-extrabold"><Money aed={pvPrice.once} /></b></div>}
                    {pvPrice.month > 0 && <div className="flex justify-between text-xs font-semibold text-gray-600"><span>Bookkeeping</span><b className="text-gray-900 font-extrabold"><Money aed={pvPrice.month}/> /mo</b></div>}
                    {pvPrice.qtr   > 0 && <div className="flex justify-between text-xs font-semibold text-gray-600"><span>VAT filing</span><b className="text-gray-900 font-extrabold"><Money aed={pvPrice.qtr}  /> /qtr</b></div>}
                    {pvPrice.year  > 0 && <div className="flex justify-between text-xs font-semibold text-gray-600"><span>Corp. Tax filing</span><b className="text-gray-900 font-extrabold"><Money aed={pvPrice.year} /> /yr</b></div>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </AdminLayout>
  );
}
